"use client"

import { XIcon } from "lucide-react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState, useTransition } from "react"

import type { ResolvedThemeTokens } from "@workspace/theming"

import { DevThemeEditor } from "./dev-theme-editor"

export interface DevTenantOption {
  slug: string
  name: string
  /** The dev host that resolves to this tenant (e.g. `demo-pizza.localhost`). */
  host: string
  /** Tenant's default locale — switch lands here directly (US-014 gate is unreliable under PPR). */
  defaultLocale: string
}

/**
 * Dev tenant switcher toolbar (US-016 / PRD §9.12). A fixed, dev-only toolbar that
 * lets a reviewer jump between seeded tenants and force scenario presets WITHOUT
 * retyping `*.localhost` hosts or query strings by hand.
 *
 * TENANT: navigates to the selected tenant's HOST (keeping the current path + port),
 * so the tenant resolves the normal way (host → tenant) and its own default locale
 * applies. This is deliberately host-based, not `?tenant=`: the `?tenant=` override
 * is dropped by the per-tenant locale gate's redirect (US-014) when the target
 * tenant doesn't offer the current locale, which would show the wrong tenant.
 *
 * SCENARIO: rewrites `?scenario=<preset>` on the current page (proxy.ts forwards it
 * as `x-scenario` in dev → the fixtures branch of the http-client, US-006/US-008).
 *
 * Rendered only when `NODE_ENV !== "production"` (the server mount guards this and
 * dynamic-imports the fixture list, so no fixture JSON ships to prod). Uses BARE
 * `next/navigation` so `usePathname()` keeps the `[locale]` prefix.
 */
/** Read a cookie value by name on the client (`""` when absent). */
function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : ""
}

export function DevToolbar({
  tenants,
  scenarios,
  layouts,
  theme,
  activeSlug,
  activeLayout,
}: {
  tenants: DevTenantOption[]
  scenarios: string[]
  /** Every known LayoutName — the layout switcher's options. */
  layouts: string[]
  /** Current tenant's resolved theme tokens — enables the live theme editor. */
  theme?: ResolvedThemeTokens
  /** Server-resolved active tenant slug — the correct selection before any switch. */
  activeSlug?: string
  /** Server-resolved active layout name — the selection before any override. */
  activeLayout?: string
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()

  // Closing is temporary (in-memory only) — reappears on page refresh, so it never
  // hides itself permanently in dev.
  const [isOpen, setIsOpen] = useState(true)

  // Active tenant + host are only known on the client (SSR has no window). The
  // sticky `dev-tenant` cookie (set below) is the source of truth for the active
  // tenant on a single-host demo deploy; fall back to the host's first DNS label
  // for real per-subdomain deploys.
  const [host, setHost] = useState("")
  const [cookieTenant, setCookieTenant] = useState("")
  const [cookieLayout, setCookieLayout] = useState("")
  useEffect(() => {
    setHost(window.location.host)
    setCookieTenant(readCookie("dev-tenant"))
    setCookieLayout(readCookie("dev-layout"))
  }, [])

  const currentLabel = host.split(":")[0]?.split(".")[0] ?? ""
  const currentTenant =
    tenants.find((t) => t.slug === cookieTenant) ??
    tenants.find((t) => t.slug === activeSlug) ??
    tenants.find(
      (t) =>
        t.slug === currentLabel || t.host.split(":")[0] === host.split(":")[0]
    )
  const currentScenario = searchParams.get("scenario") ?? ""

  function switchTenant(slug: string) {
    const target = tenants.find((t) => t.slug === slug)
    if (!target) return
    // Sticky same-origin switch: set the `dev-tenant` cookie (proxy.ts reads it
    // into x-tenant) and land on the tenant's home. Works on a single host/IP —
    // no per-tenant subdomain or wildcard DNS needed. The cookie survives link
    // navigation and the US-014 locale-gate redirect, unlike a `?tenant=` query.
    document.cookie = `dev-tenant=${encodeURIComponent(slug)}; path=/; max-age=86400; samesite=lax`
    // Land on the tenant's OWN default locale directly. Going to `/` and trusting
    // the US-014 locale-gate redirect is unreliable under PPR (the prerendered
    // shell can commit the wrong `<html lang>`), so we pick the locale here.
    window.location.assign(`/${target.defaultLocale}`)
  }

  /**
   * Sticky layout override: sets the `dev-layout` cookie (proxy.ts reads it into
   * `x-layout`, `getAppSettings` applies it over the tenant's configured layout)
   * and reloads. A cookie rather than `?layout=` for the same reason as the
   * tenant switcher — it survives ordinary navigation and the US-014 locale-gate
   * redirect. Choosing "(tenant default)" expires the cookie.
   */
  function switchLayout(value: string) {
    document.cookie = value
      ? `dev-layout=${encodeURIComponent(value)}; path=/; max-age=86400; samesite=lax`
      : "dev-layout=; path=/; max-age=0; samesite=lax"
    window.location.reload()
  }

  function switchScenario(value: string) {
    const params = new URLSearchParams(searchParams.toString())
    if (value) params.set("scenario", value)
    else params.delete("scenario")
    const query = params.toString()
    startTransition(() =>
      router.push(query ? `${pathname}?${query}` : pathname)
    )
  }

  if (!isOpen) return null

  return (
    <div
      dir="ltr"
      data-dev-toolbar=""
      className="fixed inset-x-0 bottom-0 z-[9999] flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-neutral-700 bg-neutral-900 px-3 py-2 font-mono text-xs text-neutral-100 shadow-lg"
    >
      <span className="rounded bg-amber-500 px-1.5 py-0.5 font-bold text-neutral-900">
        DEV
      </span>

      <label className="flex items-center gap-1.5">
        <span className="text-neutral-400">tenant</span>
        <select
          aria-label="Dev tenant switcher"
          value={currentTenant?.slug ?? ""}
          onChange={(e) => switchTenant(e.target.value)}
          className="rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5 text-neutral-100"
        >
          {currentTenant ? null : <option value="">— select —</option>}
          {tenants.map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5">
        <span className="text-neutral-400">layout</span>
        <select
          aria-label="Dev layout switcher"
          value={cookieLayout}
          onChange={(e) => switchLayout(e.target.value)}
          className="rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5 text-neutral-100"
        >
          <option value="">
            {activeLayout ? `${activeLayout} (tenant)` : "tenant default"}
          </option>
          {layouts.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1.5">
        <span className="text-neutral-400">scenario</span>
        <select
          aria-label="Dev scenario preset"
          value={currentScenario}
          disabled={isPending}
          onChange={(e) => switchScenario(e.target.value)}
          className="rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5 text-neutral-100"
        >
          <option value="">none</option>
          {scenarios.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      {theme ? <DevThemeEditor initialTheme={theme} /> : null}

      <a
        href="/en-GB/dev/gallery"
        className="ms-auto rounded border border-neutral-600 px-2 py-0.5 text-neutral-100 hover:bg-neutral-800"
      >
        component gallery →
      </a>

      <button
        type="button"
        aria-label="Close dev toolbar"
        title="Close dev toolbar (until page refresh)"
        onClick={() => setIsOpen(false)}
        className="rounded border border-neutral-600 p-1 text-neutral-100 hover:bg-neutral-800"
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  )
}
